import React, { useEffect, useState } from 'react'
import {
  ActivityProgress,
  CustomType,
  GoalWithProgress,
  ProgressType,
  UserCustomDailyProgress,
  UserDailyDiaryProgress,
  UserDailyProgress,
  UserProfileDto,
} from '../../services/api.types'
import makeStyles from '@material-ui/core/styles/makeStyles'
import moment from 'moment'
import YumCustomTable from '../YumCustomTable/YumCustomTable'
import { CustomTypeCategory, getCustomTypeWithLegacy, getUnit } from '../../services/CustomTypes'
import { PluginListenerHandle } from '@capacitor/core'
import { App } from '@capacitor/app'
import { convertDateToTallinnTimeZone, convertUTCToLocalDate } from '../../common/helpers/date'
import YumChallengeProgress from '../YumChallengeProgress/YumChallengeProgress'
import YumLogTable from '../YumCustomTable/YumLogTable'
import {
  getDailyValueGoal,
  getPersistenceDailyTotal,
  isActivityIndexType,
  isAutomatedType,
  isCollectiveType,
  isCustomType,
  isDistanceType,
  isPersistenceType,
  isSimpleDayType,
} from '../../common/helpers/challenges'
import YumChallengeActivityIndexProgress from '../YumChallengeProgress/components/YumChallengeActivityIndexProgress/YumChallengeActivityIndexProgress'
import YumChallengeCollectiveProgress from '../YumChallengeProgress/components/YumChallengeCollectiveProgress/YumChallengeCollectiveProgress'
import { RefreshChallengeResultsState } from '../../views/Progress/store/ProgressSagas'
import YumChallengeDailyProgress from '../YumChallengeProgress/components/YumChallengeDailyProgress/YumChallengeDailyProgress'
import { Grid } from '@material-ui/core'
import YumLogActivityWrapper from '../YumLogActivity/YumLogActivityWrapper'
import { primaryBlue } from '../../styles/material-ui-theme-v2'
import { t } from 'ttag'
import YumInsufficientPermissionsWrapper from '../YumInsufficientPermissions/YumInsufficientPermissionsWrapper'
import { isRtl } from '../../i18nInit'
import YumManualEntryFormWrapper from '../YumManualEntryForm/YumManualEntryFormWrapper'
import { PointsLogic, PointsLogicId } from '../../views/ChallengeAdmin/components/ChallengePoints/ChallengePoints'

export interface AllDailyDiary {
  [goalId: string]: UserDailyDiaryProgress[]
}
export interface AllCustomProgress {
  // TODO:
  [goalId: string]: any //UserCustomDailyProgress[]
}

export interface IStateProps {
  userDto: UserProfileDto
  goal: GoalWithProgress
  dailyDiary: UserDailyDiaryProgress[]
  allDailyDiary: AllDailyDiary
  customProgress: UserCustomDailyProgress[]
  allCustomProgress: AllCustomProgress
  progressResults: UserDailyProgress[]
  activityProgress: ActivityProgress
  showManualEntry?: boolean
  isNewLayout?: boolean
  manualEntryProgressType: CustomType
  manualEntryPointsLogicId: PointsLogicId | null
}

export interface IDispatchProps {
  refreshChallengeResults(payload: RefreshChallengeResultsState): void
  getProgress(payload?: { startDate?: Date; endDate?: Date }): void
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    // @media (min-width: 992px) {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      width: '100%',
      //gap: '20px',

      '& > div': {
        width: '50%',
      },
    },
  },
  wrapperMarginRight: {
    [theme.breakpoints.up('md')]: {
      marginRight: isRtl ? 0 : '10px',
      marginLeft: isRtl ? '10px' : 0,
    },
  },
  wrapperMarginLeft: {
    [theme.breakpoints.up('md')]: {
      marginLeft: isRtl ? 0 : '10px',
      marginRight: isRtl ? '10px' : 0,
    },
  },
  link: {
    userSelect: 'none',
    color: primaryBlue,
    fontSize: '13px',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'none',
      },
    },
    '&:focus': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'none',
      },
    },
    '&:active': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'underline !important',
      },
    },
  },
  /*link2: {
    display: 'inline-block',
    padding: '0px',
    fontWeight: 500,
    lineHeight: '16px',
    fontSize: '13px',
    width: 'auto',
    minWidth: 'auto',
    margin: '0px',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    backgroundColor: 'transparent',
    color: primaryBlue,
    borderRadius: '0px',
    //height: '20px',
  },*/
}))

export default function YumCustomEntry(props: IStateProps & IDispatchProps) {
  const {
    userDto,
    goal,
    dailyDiary,
    customProgress,
    allCustomProgress,
    activityProgress,
    progressResults,
    isNewLayout,
    refreshChallengeResults,
    getProgress,
    manualEntryProgressType,
    manualEntryPointsLogicId,
    allDailyDiary,
  } = props
  const {
    id: goalId,
    customUnit,
    dailyPersistenceGoal,
    progressType,
    participating,
    manualEntryEnabled,
    roundDailyPoints,
  } = goal
  const customType = goal.customType
  const isCustom = isCustomType(progressType)
  const isCustomCustom = customType === CustomType.CUSTOM
  const isSimpleType = isSimpleDayType(customType as unknown as CustomTypeCategory)
  const isPersistence = isPersistenceType(progressType)
  const isPersistenceCustomOrBinary =
    progressType === ProgressType.PERSISTENCE_CUSTOM || progressType === ProgressType.PERSISTENCE_CUSTOM_BINARY

  const simpleManualEntry = goal.simpleManualEntry && progressType === ProgressType.PERSISTENCE_CUSTOM

  const classes = useStyles()

  const challengeStartDate = moment(convertUTCToLocalDate(new Date(goal.startDate)))
  const challengeEndDate = moment(convertUTCToLocalDate(new Date(goal.endDate)))

  const challengeHasStarted = challengeStartDate.isBefore(new Date())
  const challengeHasEnded = challengeEndDate.isBefore(new Date())

  const gracePeriodEnd = challengeEndDate.clone().add(3, 'days')
  const gracePeriodEnded = moment().isAfter(gracePeriodEnd)

  const isActive = challengeHasStarted && !gracePeriodEnded

  const isActivityIndex = progressType === ProgressType.ALL
  const isAnyActivityIndex = [ProgressType.ALL, ProgressType.PERSISTENCE_ALL].includes(progressType) //progressType === ProgressType.ALL
  const isCollective = isCollectiveType(progressType)

  const userCantManualEntry = isAnyActivityIndex && !userDto.dateOfBirth

  // Double check
  const showLegacyProgress = isAnyActivityIndex // !isCustom //&& !isCollective

  const isPoints = goal.customType === CustomType.POINTS
  const pointsLogic = isPoints ? JSON.parse(goal.pointsLogic) : null

  const syncProgress = (preventListUpdate?: boolean) => {
    const defaultProgressType = (isAnyActivityIndex ? manualEntryProgressType : progressType) as ProgressType
    const defaultPointsLogicId = manualEntryPointsLogicId || null
    refreshChallengeResults({
      goalId,
      customType: getCustomTypeWithLegacy(customType, defaultProgressType),
      startDate: challengeStartDate.locale('en').format('YYYY-MM-DD'),
      endDate: challengeEndDate.locale('en').format('YYYY-MM-DD'),
      participating,
      isCollective,
      preventListUpdate,
      isActivityIndex: isAnyActivityIndex,
      manualEntryEnabled,
      // TODO:
      pointsLogicId: null, //defaultPointsLogicId,
      pointsLogic,
      isPoints,
    })
    if (showLegacyProgress) {
      const startDate = convertDateToTallinnTimeZone(new Date(goal.startDate))
      const endDate = convertDateToTallinnTimeZone(new Date(goal.endDate))
      getProgress({ startDate, endDate })
    }
  }

  let appStateChangeListener: PluginListenerHandle
  useEffect(() => {
    // TODO: IS THIS NEEDED?
    //syncProgress(true)

    const setListener = async () => {
      appStateChangeListener = await App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          syncProgress()
        }
      })
    }

    setListener().catch(console.error)

    return () => {
      appStateChangeListener?.remove()
    }
  }, [])

  // Update results after data sync.
  useEffect(() => {
    // This also acts as the initial trigger.
    syncProgress()
  }, [userDto.lastSync])

  //const units = getCustomUnitText(customType, customType, customUnit) as string
  //const unitsLabel = getCustomUnitLabel(customType, customType, customUnit) as string
  const unitsLabel = getUnit(progressType, customType, customUnit) as string

  const isDistance = isDistanceType(customType)
  const isAutomated = isAutomatedType(customType)

  const isManualEntryEnabled = goal.manualEntryEnabled || isPersistenceCustomOrBinary

  const hasCustomPointActivities =
    pointsLogic && pointsLogic.some((a: PointsLogic) => a.unit === CustomType.CUSTOM || !isAutomatedType(a.unit))

  const showConsistencyDailyProgress = isPersistence && (!isCustom || isAutomated) //&& challengeHasEnded
  const showCustomEntry = !isAutomated && isCustom /*&& isManualEntryEnabled*/ //&& challengeHasStarted && !gracePeriodEnded

  //const showManualEntryButton = (isAutomated && manualEntryEnabled) || (!isCustom && manualEntryEnabled) //&& challengeHasStarted && !gracePeriodEnded
  const showManualEntryButton = ((isAutomated || !isCustom) && manualEntryEnabled) || hasCustomPointActivities

  const tableDataReversed = isNewLayout && customProgress ? customProgress.slice().reverse() : customProgress

  let dailyDiaryReversed = dailyDiary ? dailyDiary.slice().reverse() : dailyDiary

  // TODO: Only for activity index
  // Convert "Progress view" data into "Custom" data.
  if (showLegacyProgress) {
    dailyDiaryReversed = progressResults
      .slice()
      .reverse()
      .map((userDailyProgress: UserDailyProgress) => {
        const manualValue = getPersistenceDailyTotal(userDailyProgress, progressType, true)
        const integrationValue = isAnyActivityIndex ? userDailyProgress.integrationTotalPercentage : manualValue
        return {
          date: userDailyProgress.date,
          integrationValue,
          manualValue: manualValue - integrationValue,
          sum: manualValue,
        } as UserDailyDiaryProgress
      })
  }

  const [showDetails, setShowDetails] = useState(false)
  const toggleShowDetails = () => {
    setShowDetails(!showDetails)
  }

  function onSubmit() {
    // Do nothing.
  }

  const customTableValueGoal = getDailyValueGoal(goal, progressType) //progressType === ProgressType.PERSISTENCE_CUSTOM ? dailyPersistenceGoal || 0 : 1

  // Don't close if activity index or points. Except if it has only one POINTS activity...
  const hasOnlyOnePointsLogicActivity =
    pointsLogic &&
    (goal.manualEntryEnabled
      ? pointsLogic.length === 1
      : pointsLogic.filter((a: PointsLogic) => a.unit === CustomType.CUSTOM || !isAutomatedType(a.unit)).length === 1)
  const autoClose =
    !isActivityIndexType(goal.progressType) && (goal.customType !== CustomType.POINTS || hasOnlyOnePointsLogicActivity)

  return (
    <>
      {isNewLayout && isAutomated ? <YumInsufficientPermissionsWrapper /> : ''}
      <div className={showConsistencyDailyProgress || showCustomEntry ? classes.wrapper : ''}>
        {showConsistencyDailyProgress ? (
          <div className={classes.wrapperMarginRight}>
            <YumChallengeDailyProgress
              goal={goal}
              dailyDiary={dailyDiary}
              progressResults={progressResults}
              customProgress={customProgress}
            />
          </div>
        ) : (
          ''
        )}

        {showCustomEntry ? (
          <div className={classes.wrapperMarginRight} style={{ marginBottom: '16px' }}>
            <YumManualEntryFormWrapper goal={goal} onSubmit={onSubmit} />
          </div>
        ) : (
          ''
        )}
        <div className={showConsistencyDailyProgress || showCustomEntry ? classes.wrapperMarginLeft : ''}>
          <YumChallengeProgress
            goal={goal}
            activityProgress={activityProgress}
            dailyDiary={dailyDiary}
            customProgress={customProgress}
            progressResults={progressResults}
            isNewLayout={isNewLayout}
            showDetails={showDetails}
            toggleShowDetails={toggleShowDetails}
          />
        </div>
      </div>

      {isActivityIndex ? <YumChallengeActivityIndexProgress activityProgress={activityProgress} /> : ''}
      {isCollective ? <YumChallengeCollectiveProgress goal={goal} activityProgress={activityProgress} /> : ''}

      {showManualEntryButton || challengeHasStarted ? (
        <Grid
          container
          justifyContent={showManualEntryButton ? 'space-between' : 'center'}
          wrap="nowrap"
          alignItems="center"
          style={{ marginBottom: '16px' }}
        >
          {showManualEntryButton && (
            <Grid item>
              <YumLogActivityWrapper goal={goal} disabled={!isActive || userCantManualEntry} autoClose={autoClose} />
            </Grid>
          )}
          {challengeHasStarted && (
            <Grid item>
              <div className={classes.link} onClick={toggleShowDetails}>
                {!showDetails ? t`Show details` : t`Hide details`}
                {/*<svg
                style={{
                  verticalAlign: 'bottom',
                  transition: 'all 0.4s ease 0s',
                  transform: `rotate(${showDetails ? -180 : 0}deg)`,
                }}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8 11L4 5H12L8 11Z" fill="#006FFF" />
              </svg>*/}
              </div>
            </Grid>
          )}
        </Grid>
      ) : (
        ''
      )}

      {isNewLayout && (
        <div style={{ marginBottom: '16px', borderBottom: '1px solid #E3E3E8' }}>
          {showDetails && (
            <>
              {(isCustomCustom || isPoints) && !showLegacyProgress ? (
                <YumCustomTable
                  data={tableDataReversed}
                  valueGoal={customTableValueGoal}
                  unitsLabel={unitsLabel}
                  units={isAnyActivityIndex ? '%' : ''}
                  binary={progressType === ProgressType.PERSISTENCE_CUSTOM_BINARY || simpleManualEntry}
                  showDigits={isDistance}
                  isNewLayout={isNewLayout}
                  pointsLogic={pointsLogic}
                  allDailyDiary={allDailyDiary}
                  allCustomProgress={allCustomProgress}
                  manualEntryEnabled={manualEntryEnabled}
                  roundDailyPoints={roundDailyPoints}
                />
              ) : (
                <YumLogTable
                  data={dailyDiaryReversed}
                  valueGoal={isPersistence ? dailyPersistenceGoal || 0 : 1}
                  units={isAnyActivityIndex ? t`Activity index` : unitsLabel /*units*/}
                  showDigits={isDistance}
                  manualEntryEnabled={manualEntryEnabled}
                  suffix={isAnyActivityIndex ? '%' : ''}
                  isBinary={isSimpleType || simpleManualEntry}
                />
              )}
            </>
          )}
        </div>
      )}
    </>
  )
}
